// The pipeline's pure rules (spec 09 §3.11, §4.14, §4.15; D-312), unit-tested
// in `__tests__/pipeline-lib.test.ts`. Nothing here renders, fetches or
// navigates.
//
// EVERY KEY IS A LITERAL. The refusal sentences, the deal statuses, the four
// lost reasons and the task kinds are chosen by a `switch` that calls `t` with
// a literal key in each arm, rather than by building `sales.pipeline.refusal.
// ${code}`. `tools/ci/i18n-parity.ts` can only check a literal key against the
// dictionary, and a computed family needs its own exhaustiveness entry, which
// this wave's file owners do not hold (WP-F's hand-off). A code the switch
// does not know falls back to the verb's own failure sentence.
//
// NOTHING HERE ADDS MONEY UP. The board's column totals, the forecast's cells
// and each deal's weighted value are the server's, computed once per deal by
// `sales_weighted_minor` and summed there (D-312 ruling 10). This file places
// the server's rows in a range and formats them; it never multiplies,
// divides or sums a money figure, so the browser cannot round one deal a
// second way.

import type { TFunction } from "i18next";
import { uiLocale } from "../../../../lib/locale-bundle";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { decimalToMinor } from "./price-import";
import { minorUnitDigits, normalizeTypedDecimal } from "./money";

// ---------------------------------------------------------------------------
// Wire queries
// ---------------------------------------------------------------------------

/** An object with every `undefined` member left out, typed so: the shape a
 * generated query type takes under `exactOptionalPropertyTypes`. */
export type Defined<T> = { [K in keyof T]?: Exclude<T[K], undefined> };

export function definedOnly<T extends object>(value: T): Defined<T> {
  const out: Record<string, unknown> = {};
  for (const [key, member] of Object.entries(value)) {
    if (member !== undefined) out[key] = member;
  }
  return out as Defined<T>;
}

// ---------------------------------------------------------------------------
// Stage names
// ---------------------------------------------------------------------------

export interface NamedStage {
  readonly name: string;
  readonly name_i18n: { readonly en: string } & Readonly<Record<string, string | undefined>>;
}

/** A stage's name in the reader's language, from the pack's bundle, never
 * from the dictionaries (spec 09 §4.14). The language goes through the
 * registry (D-430), so zh-TW reads the Traditional name, never the
 * Simplified one. A locale the bundle does not carry reads the English name. */
export function stageName(stage: NamedStage, language: string): string {
  const own = stage.name_i18n[uiLocale(language) as keyof NamedStage["name_i18n"]];
  if (own !== undefined && own.trim().length > 0) return own;
  return stage.name_i18n.en || stage.name;
}

// ---------------------------------------------------------------------------
// Months (`YYYY-MM`, the contract's `SalesMonth`)
// ---------------------------------------------------------------------------

const MONTH_RE = /^(\d{4})-(0[1-9]|1[0-2])$/u;

/** `YYYY-MM` plus `delta` months, or undefined for a malformed month. */
export function addMonths(month: string, delta: number): string | undefined {
  const match = MONTH_RE.exec(month);
  if (match === null) return undefined;
  const index = Number(match[1]) * 12 + (Number(match[2]) - 1) + delta;
  const year = Math.floor(index / 12);
  const mon = (index % 12) + 1;
  return `${String(year).padStart(4, "0")}-${String(mon).padStart(2, "0")}`;
}

/** Every month from `from` to `to`, both included, or none when the range
 * runs backwards or either end is malformed. Capped at 25 entries, the most
 * a forecast range can hold. */
export function monthRange(from: string, to: string): string[] {
  const out: string[] = [];
  let cursor: string | undefined = from;
  if (MONTH_RE.exec(from) === null || MONTH_RE.exec(to) === null) return out;
  while (cursor !== undefined && cursor <= to && out.length < 25) {
    out.push(cursor);
    cursor = addMonths(cursor, 1);
  }
  return out;
}

/**
 * The quote statuses whose deal can no longer change, the server's own set
 * (`apps/api/src/modules/sales/quotes/quote-state.ts` TERMINAL): both the link
 * and the unlink answer 409 `QUOTE_TERMINAL` for them. `expired` is not in it,
 * because a revision re-opens an expired quote, and the server links and
 * unlinks one. Every surface that offers Link or Unlink reads this one set.
 */
export const QUOTE_FINISHED_STATUSES: ReadonlySet<string> = new Set([
  "accepted",
  "converted",
  "rejected",
  "cancelled",
]);

/** May this quote's deal change (pure)? Not once the quote is finished. */
export function quoteDealChangeable(status: string): boolean {
  return !QUOTE_FINISHED_STATUSES.has(status);
}

/** A month in words ("October 2026", «октябрь 2026 г.»), formatted at UTC so
 * no viewer's zone can move it into its neighbour. */
export function formatMonth(month: string, language: string): string {
  const match = MONTH_RE.exec(month);
  if (match === null) return month;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
  try {
    return new Intl.DateTimeFormat(language, {
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    }).format(date);
  } catch {
    return month;
  }
}

// ---------------------------------------------------------------------------
// Board order
// ---------------------------------------------------------------------------

export interface BoardOrdered {
  readonly id: string;
  readonly expected_close_month: string;
  readonly created_at: string;
}

/** The board's order inside a column (spec 09 §5's board row): close month,
 * then `created_at`, then `id`. The board offers no reordering, so a card a
 * move lands in a column takes the place this order gives it. */
export function boardOrder(a: BoardOrdered, b: BoardOrdered): number {
  if (a.expected_close_month !== b.expected_close_month) {
    return a.expected_close_month < b.expected_close_month ? -1 : 1;
  }
  if (a.created_at !== b.created_at) return a.created_at < b.created_at ? -1 : 1;
  if (a.id === b.id) return 0;
  return a.id < b.id ? -1 : 1;
}

// ---------------------------------------------------------------------------
// The words for enums
// ---------------------------------------------------------------------------

export type DealStatus = "open" | "won" | "lost";

export function dealStatusLabel(t: TFunction, status: DealStatus): string {
  switch (status) {
    case "open":
      return t("sales.pipeline.status.open");
    case "won":
      return t("sales.pipeline.status.won");
    case "lost":
      return t("sales.pipeline.status.lost");
  }
}

export type LostReasonCode = "price" | "lead_time" | "stock" | "other";

export const LOST_REASON_CODES: readonly LostReasonCode[] = [
  "price",
  "lead_time",
  "stock",
  "other",
];

/** §2.12's four words, which a lost deal and a rejected quote share (D-312
 * ruling 9). */
export function lostReasonLabel(t: TFunction, code: LostReasonCode): string {
  switch (code) {
    case "price":
      return t("sales.composer.rejectReasonPrice");
    case "lead_time":
      return t("sales.composer.rejectReasonLeadTime");
    case "stock":
      return t("sales.composer.rejectReasonStock");
    case "other":
      return t("sales.composer.rejectReasonOther");
  }
}

export function isLostReasonCode(value: unknown): value is LostReasonCode {
  return typeof value === "string" && (LOST_REASON_CODES as readonly string[]).includes(value);
}

export type TaskKindCode = "call" | "meeting" | "todo";

export function taskKindLabel(t: TFunction, kind: TaskKindCode): string {
  switch (kind) {
    case "call":
      return t("sales.tasks.kind.call");
    case "meeting":
      return t("sales.tasks.kind.meeting");
    case "todo":
      return t("sales.tasks.kind.todo");
  }
}

// ---------------------------------------------------------------------------
// Refusals
// ---------------------------------------------------------------------------

/**
 * The remedy a PIPELINE_NOT_CONFIGURED refusal names (D-343). The server says
 * the stages arrive with the industry pack update; the board offers that
 * update to a reader who holds `onboarding.install_pack` and tells everyone
 * else who can apply it. `undefined` for any other refusal, or a server that
 * names none.
 */
export function pipelineRemedyOf(error: unknown): "apply_pack_update" | undefined {
  if (!(error instanceof RequestError) || error.code !== "PIPELINE_NOT_CONFIGURED") {
    return undefined;
  }
  const problem = error.problem as Record<string, unknown> | undefined;
  return problem?.["remedy"] === "apply_pack_update" ? "apply_pack_update" : undefined;
}

/** The problem `code` of a refused write, if the server answered at all. */
export function refusalCode(error: unknown): string | undefined {
  return error instanceof RequestError ? error.code : undefined;
}

/** `extensions.current_stage_id` on `OPPORTUNITY_STAGE_CHANGED`. The problem
 * carries it at the top level (`OpportunityMoveConflictProblem`). */
export function currentStageIdOf(error: unknown): string | undefined {
  if (!(error instanceof RequestError)) return undefined;
  const problem = error.problem as Record<string, unknown> | undefined;
  const direct = problem?.["current_stage_id"];
  if (typeof direct === "string") return direct;
  const extensions = problem?.["extensions"];
  if (typeof extensions === "object" && extensions !== null) {
    const nested = (extensions as Record<string, unknown>)["current_stage_id"];
    if (typeof nested === "string") return nested;
  }
  return undefined;
}

/** Is this a 403 naming `sales.read_value` (a value sent by a non-holder)? */
function isValueRefusal(error: RequestError): boolean {
  const problem = error.problem as Record<string, unknown> | undefined;
  return error.httpStatus === 403 && problem?.["missing_permission"] === "sales.read_value";
}

/**
 * The sentence a refused deal write reads as (D-225: no server prose).
 * `stageName` names the stage `OPPORTUNITY_STAGE_CHANGED` points at; when it
 * cannot, the sentence says only that someone moved the deal first. `fallback`
 * is the verb's own failure sentence, used for any code the switch does not
 * know and for a request that never reached the server.
 */
export function refusalText(
  t: TFunction,
  error: unknown,
  fallback: string,
  stageNameOf?: (stageId: string) => string | undefined,
): string {
  if (!(error instanceof RequestError)) return fallback;
  if (isValueRefusal(error)) return t("sales.pipeline.refusal.valueForbidden");
  switch (error.code) {
    case "OPPORTUNITY_STAGE_CHANGED": {
      const id = currentStageIdOf(error);
      const name = id === undefined ? undefined : stageNameOf?.(id);
      // A stage this screen cannot name (a read older than the stage) keeps
      // the verb's own sentence rather than printing a blank where a name goes.
      return name === undefined
        ? fallback
        : t("sales.pipeline.refusal.OPPORTUNITY_STAGE_CHANGED", { stage: name });
    }
    case "OPPORTUNITY_NOT_OPEN":
      return t("sales.pipeline.refusal.OPPORTUNITY_NOT_OPEN");
    case "OPPORTUNITY_NOT_CLOSED":
      return t("sales.pipeline.refusal.OPPORTUNITY_NOT_CLOSED");
    case "OPPORTUNITY_NOT_FOUND":
      return t("sales.pipeline.refusal.OPPORTUNITY_NOT_FOUND");
    case "PIPELINE_STAGE_NOT_FOUND":
      return t("sales.pipeline.refusal.PIPELINE_STAGE_NOT_FOUND");
    case "PIPELINE_NOT_CONFIGURED":
      return t("sales.pipeline.refusal.PIPELINE_NOT_CONFIGURED");
    case "OPPORTUNITY_OWNER_NOT_ELIGIBLE":
      return t("sales.pipeline.refusal.OPPORTUNITY_OWNER_NOT_ELIGIBLE");
    case "OPPORTUNITY_CUSTOMER_MISMATCH":
      return t("sales.pipeline.refusal.OPPORTUNITY_CUSTOMER_MISMATCH");
    case "OPPORTUNITY_WON_BY_ORDER":
      return t("sales.pipeline.refusal.OPPORTUNITY_WON_BY_ORDER");
    case "CUSTOMER_ARCHIVED":
      return t("sales.pipeline.refusal.CUSTOMER_ARCHIVED");
    case "CUSTOMER_NOT_FOUND":
      return t("sales.pipeline.refusal.CUSTOMER_NOT_FOUND");
    case "QUOTE_NOT_FOUND":
      return t("sales.pipeline.refusal.QUOTE_NOT_FOUND");
    case "ORDER_NOT_FOUND":
      return t("sales.pipeline.refusal.ORDER_NOT_FOUND");
    case "QUOTE_TERMINAL":
      return t("sales.pipeline.refusal.QUOTE_TERMINAL");
    case "QUOTE_LINKED_ELSEWHERE":
      return t("sales.pipeline.refusal.QUOTE_LINKED_ELSEWHERE");
    case "QUOTE_NOT_CONVERTED":
      return t("sales.pipeline.refusal.QUOTE_NOT_CONVERTED");
    case "ORDER_NOT_CONFIRMED":
      return t("sales.pipeline.refusal.ORDER_NOT_CONFIRMED");
    case "ORDER_TERMINAL":
      return t("sales.pipeline.refusal.ORDER_TERMINAL");
    case "ORDER_WON_ELSEWHERE":
      return t("sales.pipeline.refusal.ORDER_WON_ELSEWHERE");
    case "CONVERSATION_NOT_FOUND":
      return t("sales.pipeline.refusal.CONVERSATION_NOT_FOUND");
    case "SOURCE_CONVERSATION_UNVERIFIABLE":
      return t("sales.pipeline.refusal.SOURCE_CONVERSATION_UNVERIFIABLE");
    default:
      return error.httpStatus === 403 ? t("sales.pipeline.refusal.forbidden") : fallback;
  }
}

/** The refusal sentence and the request id to print beside it. */
export interface Refusal {
  readonly text: string;
  readonly requestId: string | undefined;
}

export function refusalOf(
  t: TFunction,
  error: unknown,
  fallback: string,
  stageNameOf?: (stageId: string) => string | undefined,
): Refusal {
  return { text: refusalText(t, error, fallback, stageNameOf), requestId: requestIdOf(error) };
}

// ---------------------------------------------------------------------------
// The value box
// ---------------------------------------------------------------------------

/**
 * What the reader typed in a deal's value box, as integer minor units in the
 * deal's currency: a number, `null` for an empty box (nobody estimated it,
 * which no reader counts as zero, D-312 ruling 7), or `undefined` for text
 * that is not an amount. The locale's own separators are accepted, as every
 * money box in this area accepts them (task #204).
 */
export function parseValueInput(
  text: string,
  currency: string,
  language: string,
): number | null | undefined {
  if (text.trim().length === 0) return null;
  const canonical = normalizeTypedDecimal(text, language);
  return decimalToMinor(canonical, minorUnitDigits(currency, language));
}

// ---------------------------------------------------------------------------
// The next step (spec 09 §3.12)
// ---------------------------------------------------------------------------

export interface NextActivityWire {
  readonly kind: TaskKindCode;
  readonly due_on: string;
  readonly overdue: boolean;
  readonly due_today: boolean;
}

export type NextStep =
  | { readonly kind: "none" }
  | { readonly kind: "flag" }
  | {
      readonly kind: "task";
      readonly taskKind: TaskKindCode;
      readonly dueOn: string;
      readonly band: "overdue" | "today" | "ahead";
    };

/**
 * What a deal's or a customer's next-step cell shows. An open task wins over
 * the flag, because an overdue task still counts as the next step and the
 * flag shows it in red (§3.12); the flag without a task is "No next step";
 * and a closed deal, whose `needs_next_step` is false and which may still
 * carry a task, shows the task or nothing.
 */
export function nextStepOf(
  next: NextActivityWire | null | undefined,
  needsNextStep: boolean,
): NextStep {
  if (next !== null && next !== undefined) {
    return {
      kind: "task",
      taskKind: next.kind,
      dueOn: next.due_on,
      band: next.overdue ? "overdue" : next.due_today ? "today" : "ahead",
    };
  }
  return needsNextStep ? { kind: "flag" } : { kind: "none" };
}

// ---------------------------------------------------------------------------
// The forecast's tables
// ---------------------------------------------------------------------------

export interface ForecastRowWire {
  readonly close_month: string;
  readonly currency: string;
  readonly open_count: number;
  readonly unvalued_count: number;
  readonly open_value_minor: number;
  readonly weighted_value_minor: number;
  readonly won_count: number;
  readonly won_unvalued_count: number;
  readonly won_cancelled_count: number;
  readonly won_value_minor: number;
}

export interface ForecastSlippedWire {
  readonly currency: string;
  readonly open_count: number;
  readonly unvalued_count: number;
  readonly open_value_minor: number;
  readonly weighted_value_minor: number;
}

export interface ForecastMonth {
  readonly month: string;
  /** The server's row, or undefined for a month it holds no deal in (the
   * rows are sparse; the screen reads an absent month as zero deals). */
  readonly row: ForecastRowWire | undefined;
}

export interface ForecastTable {
  readonly currency: string;
  readonly slipped: ForecastSlippedWire | undefined;
  readonly months: readonly ForecastMonth[];
  /** The won deals whose order was cancelled, across the range, as served
   * (a count, not money). */
  readonly wonCancelled: number;
}

/**
 * One table per currency (spec 09 §4.14's forecast): the slipped row first,
 * then one row per month of the range, each the server's own row or an empty
 * month. Currencies come in the order they first appear, slipped first, so
 * the page does not reshuffle between reads. No money is added here: the
 * cancelled-win count is a count of deals.
 */
export function forecastTables(
  rows: readonly ForecastRowWire[],
  slipped: readonly ForecastSlippedWire[],
  months: readonly string[],
): ForecastTable[] {
  const currencies: string[] = [];
  const see = (currency: string) => {
    if (!currencies.includes(currency)) currencies.push(currency);
  };
  for (const s of slipped) see(s.currency);
  for (const r of rows) see(r.currency);
  return currencies.map((currency) => {
    const byMonth = new Map<string, ForecastRowWire>();
    let wonCancelled = 0;
    for (const r of rows) {
      if (r.currency !== currency) continue;
      byMonth.set(r.close_month, r);
      wonCancelled += r.won_cancelled_count;
    }
    return {
      currency,
      slipped: slipped.find((s) => s.currency === currency),
      months: months.map((month) => ({ month, row: byMonth.get(month) })),
      wonCancelled,
    };
  });
}

// ---------------------------------------------------------------------------
// Reopen (spec 09 §3.11, §4.14's Deal bullet)
// ---------------------------------------------------------------------------

export interface ReopenFacts {
  readonly status: DealStatus;
  readonly won_via: "manual" | "quote_accepted" | null;
  readonly won_order_cancelled: boolean | null;
}

/**
 * Whether the deal page offers Reopen. A lost deal and a deal a person won
 * reopen. A deal the customer's acceptance won reopens once its order is
 * cancelled, so Reopen is withheld while the served `won_order_cancelled` says
 * the order stands. When that flag is null, the reader's order door refuses
 * them and the page cannot know, so it offers Reopen and lets the server's
 * answer decide (D-312 ruling 21).
 */
export function reopenOffered(deal: ReopenFacts): boolean {
  if (deal.status === "open") return false;
  if (deal.status === "lost") return true;
  if (deal.won_via === "quote_accepted") return deal.won_order_cancelled !== false;
  return true;
}
